import {
  LinkedinFilled,
  InstagramFilled,
  FacebookFilled,
  TikTokFilled,
  YoutubeFilled,
  GithubFilled,
  WhatsAppOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { SiSnapchat } from "react-icons/si";

// Same keys, same order as the server's SocialLinkSanitizer.Platforms. `base` is what a bare
// username is appended to; WhatsApp and website are handled separately in normalizeSocialInput.
// `color` is each platform's brand color, used to highlight the icon once a link is saved for it.
export const PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", Icon: LinkedinFilled, placeholder: "linkedin.com/in/username", base: "https://www.linkedin.com/in/", color: "#0A66C2" },
  { key: "instagram", label: "Instagram", Icon: InstagramFilled, placeholder: "@username", base: "https://www.instagram.com/", color: "#E4405F" },
  { key: "snapchat", label: "Snapchat", Icon: SiSnapchat, placeholder: "@username", base: "https://www.snapchat.com/add/", color: "#FFFC00" },
  { key: "facebook", label: "Facebook", Icon: FacebookFilled, placeholder: "facebook.com/username", base: "https://www.facebook.com/", color: "#1877F2" },
  { key: "tiktok", label: "TikTok", Icon: TikTokFilled, placeholder: "@username", base: "https://www.tiktok.com/@", color: "#010101" },
  { key: "youtube", label: "YouTube", Icon: YoutubeFilled, placeholder: "@channel", base: "https://www.youtube.com/@", color: "#FF0000" },
  { key: "github", label: "GitHub", Icon: GithubFilled, placeholder: "github.com/username", base: "https://github.com/", color: "#181717" },
  { key: "whatsapp", label: "WhatsApp", Icon: WhatsAppOutlined, placeholder: "+966 5X XXX XXXX", color: "#25D366" },
  { key: "website", label: "Website", Icon: GlobalOutlined, placeholder: "www.example.com", color: "#4F46E5" },
];

const platformOf = (key) => PLATFORMS.find((p) => p.key === key);

// http(s) with a real host (it has a dot, so "https://jane" is not a link)
const isWebUrl = (value) => {
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:") && url.hostname.includes(".");
  } catch {
    return false;
  }
};

// Turns what a person types (a username, @username, a phone number, a link with or without
// "https://") into a full link, or "" when it cannot be made into a safe one. This is only a
// convenience for the user; the server validates again on save.
export const normalizeSocialInput = (key, raw) => {
  const platform = platformOf(key);
  const value = (raw || "").trim();
  if (!platform || !value) return "";

  if (key === "whatsapp" && !/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    const digits = value.replace(/\D/g, "");
    return digits.length >= 7 ? `https://wa.me/${digits}` : "";
  }

  if (/\s/.test(value)) return "";

  // anything with a scheme must be http(s), so javascript:, data: and ftp: are refused
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    return /^https?:\/\//i.test(value) && isWebUrl(value) ? value : "";
  }

  if (key === "website") {
    const withScheme = `https://${value}`;
    return isWebUrl(withScheme) ? withScheme : "";
  }

  // "www.x.com/..." or "linkedin.com/in/jane": a link without its scheme.
  // A bare "jane.doe" is a username (Instagram handles contain dots).
  if (/^www\./i.test(value) || value.includes("/")) {
    const withScheme = `https://${value}`;
    return isWebUrl(withScheme) ? withScheme : "";
  }

  const handle = value.replace(/^@/, "");
  return /^[A-Za-z0-9._-]+$/.test(handle) ? platform.base + handle : "";
};

export const getSocialLinks = (formData) =>
  (Array.isArray(formData?.socialLinks) ? formData.socialLinks : []).filter(
    (link) => link && platformOf(link.platform) && typeof link.url === "string" && link.url
  );

// New formData with one platform's link set (or removed when url is ""), one link per
// platform, always in the order of PLATFORMS so the card shows them in a steady order.
export const setSocialLink = (formData, key, url) => {
  const others = getSocialLinks(formData).filter((link) => link.platform !== key);
  const next = url ? [...others, { platform: key, url }] : others;
  const order = (platform) => PLATFORMS.findIndex((p) => p.key === platform);
  next.sort((a, b) => order(a.platform) - order(b.platform));
  return { ...formData, socialLinks: next };
};
